import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Drawer, Input, InputNumber, Popover, Select, Space, Tabs } from 'antd';

import { InputSelect, InputTime, optionSearchFilter } from '../Input';
import DataTypeBadge from './DataTypeBadge';
import { RecordQualifier } from './RecordBadge';
import FilterDateValue from './FilterDateValue';
import ItemLookupSelect from './ItemLookupSelect';
import MeasurementValueInput from './MeasurementValueInput';
import CurrencyValueInput from './CurrencyValueInput';
import UserPickerSelect from '../users/UserPickerSelect';
import IdValueCheck from './IdValueCheck';
import TagSelect from '../tags/TagSelect';
import useCollaborators from '../../hooks/useCollaborators';
import { ME_VALUE } from '../../utils/userElements';
import RecentFilters from './RecentFilters';
import FilterIdsLine from './FilterIdsLine';
import { CopyTextButton } from './ShowAsText';
import { booleanLabels, getNumericInputMode } from '../../utils/elements';
import { parseNumberEntry } from '../../utils/numberEntry';
import { CLOSE, PLUS } from '../icons';
import {
	canonicalize,
	createRow,
	fieldKindOf,
	fieldsHaveAccountLink,
	findField,
	isDatePartField,
	isMeValue,
	isNullaryOperator,
	isListOperator,
	isTextMatchOperator,
	isRefRaw,
	isSpanRow,
	makeRefRaw,
	makeValueNode,
	operatorsForField,
	operatorsForRow,
	refSupportsOperator,
	refTargetsFor,
	parseForBuilder,
	pathKey,
	queryUsesMe,
	rawValueOf,
	retargetValue,
	rowCondition,
	conditionValueLabel,
	describeCondition,
	elementFieldEntries,
	entryColumnFields,
	isEntriesCmp,
	isEntriesField,
	newEntryRowFor,
	seedRowsForGroup,
} from '../../utils/kqlBuilder';
import { spanLabel } from '../../utils/datePresets';
import {
	GROUP_MATCHES,
	MATCH_ALL,
	MATCH_NONE,
	TOP_MATCHES,
	allRows,
	groupMatchFor,
	isGroupItem,
	makeGroup,
	mapRows,
	removeRow,
	updateGroup,
} from '../../utils/conditionGroups';
import { readFilterGroups, writeFilterGroups } from '../../utils/filterGroups';
import { isFacetField, quickFilterInScope } from '../../utils/facets';
import { recordRecentFilter } from '../../utils/recentFilters';
import { idsNamedIn } from '../../utils/filterIds';
import useQueryLookupTitles from '../../hooks/useQueryLookupTitles';
import { serialize, isElementIdSegment } from '../../utils/kql';
import { fromLabel } from '../../utils/linkedRecords';
import {
	checkConditionValues,
	checkOperator,
	checkQueryOperators,
	checkQueryValues,
	displayedCheck,
	idValueKindFor,
	isFlagged,
	replaceQueryValue,
	resolveQueryLabels,
	resolveQueryTextLabels,
	userVocabularyOf,
} from '../../utils/idValueValidation';
import { isRestrictedTag } from '../../utils/tags';
import { ratingScale } from '../../utils/rating';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import useWindowClass from '../../hooks/useWindowClass';

/**
 * What the builder says over a filter it cannot show as rows: one nested
 * deeper than one level of groups, or holding a condition no row shows. Its
 * rows are read-only and its text is one click away (KPR-978).
 */
export const ADVANCED_NOTE = 'This filter goes deeper than the builder shows, so it is edited as text.';
/**
 * A field's choices as Select options. Most are `{ value, label }`; an element
 * written long ago may hold bare strings, which are their own value and label
 * (an antd option must be an object).
 */
const choiceSelectOptions = choices => (Array.isArray(choices) ? choices : [])
	.filter(choice => choice !== null && choice !== undefined && choice !== '')
	.map(choice => (typeof choice === 'object' ? choice : { value: String(choice), label: String(choice) }));

/** The words of a join (KPR-978, as ConditionBuilder's): "Match all of these", a group's "none of these". */
const MATCH_WORDS = { all: 'all', any: 'any', [MATCH_NONE]: 'none' };
const TOP_MATCH_OPTIONS = TOP_MATCHES.map(value => ({ value, label: MATCH_WORDS[value] }));
const GROUP_MATCH_OPTIONS = GROUP_MATCHES.map(value => ({ value, label: MATCH_WORDS[value] }));
/** What the builder says when `disallowMe` and the draft names `me`. */
export const ME_DISALLOWED_NOTE = 'This filter cannot use me: it is evaluated for everyone at once, so it has nobody to be.';
/** What the builder says while a condition's operator is one the API refuses (T7, ruled b26). */
export const REFUSED_OPERATOR_NOTE = 'Change the flagged condition to apply this filter.';
/** The tag picker's empty state where only restricted tags may be picked (T7). */
export const NO_RESTRICTED_TAGS = 'No restricted tags yet. Mark a tag Restricted in Settings › Tags to use it here.';

/** The two things a row's right-hand side can be. */
export const VALUE_SOURCE_FIELD = 'field';
/** What the "another field" toggle says, and what its picker asks for. */
export const REF_TOGGLE_LABEL = 'Field';
export const REF_PLACEHOLDER = 'Compare with…';

/** How many elements a facet stands for, in words, for its option's tooltip. */
const facetTitle = field => {
	const names = [...new Set((field.memberElements || []).map(entry => entry.name))];
	if (!names.length) return field.label;
	return `${field.label} — matches ${names.join(', ')}`;
};

/**
 * Field options for the row select: the type's own badge beside its label,
 * grouped Facets · Fields · System. Facets lead because they are the BROADER
 * question — "which person?" before "which person in this one field?" — and a
 * reader scanning for a name should meet the one that spans every card first.
 *
 * `scopeCardIds` SECTIONS the Fields group into "in view" and "other cards"
 * and does nothing else. THE MENU IS NEVER NARROWED, and that is deliberate:
 * this is the door that expresses anything. Removing an option would break
 * authoring a condition on the card you are about to switch to, and would
 * strand a row whose field left scope while it was open. Narrowing the CHIPS
 * and narrowing the MENU are different questions — the chip row is a shortlist
 * of the usual questions, the builder is the whole vocabulary.
 */
const buildFieldOptions = (fields, scopeCardIds = null, { keepElementNames = false } = {}) => {
	const facets = [];
	const elements = [];
	const otherElements = [];
	const hops = [];
	const system = [];
	const parts = [];
	const sectioned = scopeCardIds instanceof Set;
	// Option value → the field a row on it edits: an element is offered by
	// its id (KPR-238 P4), so the value is not always a field's key.
	const byValue = new Map();
	(fields || []).forEach(field => {
		// Known so a condition on it reads and checks, never offered as a row
		// (the `card` field: the toolbar's card picker owns that question).
		if (field.notOffered) return;
		// An element is offered as the element itself: one definition by its
		// id, several each by its id and card, and "any of these cards"
		// (elementFieldEntries). Everything else is offered as it is.
		elementFieldEntries(field, fields, { keepNames: keepElementNames }).forEach((entry) => {
			if (byValue.has(entry.key)) return;
			byValue.set(entry.key, entry.field);
			const option = fieldOption(field, entry);
			const facet = isFacetField(field);
			if (facet) facets.push(option);
			// A part of a date (`Created · Day of the week`): a section of its own.
			else if (isDatePartField(field)) parts.push(option);
			else if (field.hop) hops.push(option);
			else if (field.system) system.push(option);
			else if (sectioned && !quickFilterInScope(field, scopeCardIds)) otherElements.push(option);
			else elements.push(option);
		});
	});
	const groups = [];
	if (facets.length) groups.push({ label: 'Facets', options: facets });
	if (elements.length) groups.push({ label: sectioned ? 'Fields · in view' : 'Fields', options: elements });
	if (otherElements.length) groups.push({ label: 'Fields · other cards', options: otherElements });
	if (hops.length) groups.push({ label: 'Through a lookup · linked collections', options: hops });
	if (system.length) groups.push({ label: 'System', options: system });
	if (parts.length) groups.push({ label: 'Day of the week, hour, month', options: parts });
	return { groups, byValue };
};

const fieldOptionCaches = { named: new WeakMap(), byId: new WeakMap() };

/**
 * `buildFieldOptions` for the columns of a repeating group (KPR-683),
 * remembered per column list — it is asked on every render. A column is
 * offered by its id, or by its name where the text keeps names.
 */
const columnFieldOptions = (columns, keepElementNames) => {
	const cache = keepElementNames ? fieldOptionCaches.named : fieldOptionCaches.byId;
	if (!cache.has(columns)) cache.set(columns, buildFieldOptions(columns, null, { keepElementNames }));
	return cache.get(columns);
};

/** One option of the field menu: `entry` is what elementFieldEntries offers for `field`. */
const fieldOption = (field, entry) => {
	const facet = isFacetField(field);
	// The annotation matters because a condition on a partial element hides
	// every item of the card types that never declare it. (A definition
	// named with its card already says whose it is.)
	const named = entry.label === field.label;
	const partial = named ? !!field.partial : !!entry.field.partial;
	const text = partial && named ? `${entry.label} · some card types` : entry.label;
	const cardLabels = entry.field.cardLabels || field.cardLabels || [];
	// A HOP (a lookup walked into another collection's card) says whose
	// fields these are: "Visit › Status" then the collection's mark —
	// its stacked tile and name, the qualified badge's segment.
	const hopHint = field.hop ? fromLabel(field.hop.name) : null;
	return {
		value: entry.key,
		label: (
			<span className="list-filter__field-option">
				<DataTypeBadge dataType={field.badgeType || entry.field.dataType || field.dataType} iconOnly />
				<span className="list-filter__field-option-label">{text}</span>
				{field.hop ? (
					<RecordQualifier
						// The lender's name when the API gave one, else its id
						// (the resolver names it, or the mark stays off).
						collection={field.hop.name ? { _id: field.hop.collectionId, name: field.hop.name } : field.hop.collectionId}
						size="xs"
						note={identity => `Read in ${identity.name}, through your own access there`}
						className="list-filter__field-hop"
					/>
				) : null}
			</span>
		),
		// A ReactNode label is not searchable — the typeahead reads this.
		searchText: hopHint ? `${text} ${hopHint}` : text,
		title: facet
			? facetTitle(field)
			: field.hop
				? `${field.label} — read in ${field.hop.name || 'another collection'}, through your own access there`
				: (partial ? `${entry.label} — only on ${cardLabels.join(', ')}` : entry.label),
	};
};

/** What the builder says when it holds no rows and no subject was named. */
export const DEFAULT_EMPTY_TEXT = 'No conditions yet — every record is shown.';
/** The advanced tab's example query when no subject-specific one is given. */
export const DEFAULT_PLACEHOLDER = 'name ~ report and created > -3mo';

/**
 * The list filter builder, shared by every list page (items, cards,
 * collections): condition rows, plus an "Edit as text" tab holding the
 * canonical KQL text. The two stay in sync — rows serialize to text on every
 * edit, and text parses back into rows whenever it fits the builder's shape.
 * A query that does not fit leaves the rows read-only rather than silently
 * rewriting what the user wrote.
 *
 * ONE LEVEL OF GROUPS (KPR-978). The top line reads "Match [all | any] of
 * these" (`and` / `or`), and "Add a group" gathers rows one level down, each
 * group with its own all / any / none (`(a and b)`, `(a or b)`, `not (a or
 * b)`). The shape is utils/conditionGroups.js, which ConditionBuilder shares
 * (KPR-951); FilterBuilder's own reading of it — a date span, "any of these
 * cards" and a repeating group's `has` are each one row — is
 * utils/filterGroups.js. A group's rows are drawn by the same row renderer as
 * the top line's, so every rule a host sets (no `me`, restricted tags only,
 * element names kept) holds inside a group exactly as outside one. Anything
 * deeper than one level stays read-only, with its text one click away.
 *
 * The subject is entirely in `fields` (see `buildFilterFields` and
 * `listFilterFields.js`); the three copy/identity props let a page name its
 * records: `emptyText` under an empty builder, `placeholder` in the advanced
 * tab, `triggerId` on the "+ Filter" button so a keyboard shortcut can open it.
 *
 * THREE THINGS THE ROWS KNOW that the grammar does not:
 * - A **lookup** value is an ITEM, so its row gets the item picker
 *   (`ItemLookupSelect`) whenever the field names a card and the page names a
 *   collection; the free-text box stays as the fallback, because a title match
 *   is a real condition the API resolves and a card bigger than one page
 *   cannot be picked from.
 * - A **date** value is usually a PERIOD, so its row gets the preset menu
 *   (`FilterDateValue`) and may become a SPAN row — one row, two conditions.
 * - A **phone** is not a small desktop: below `md` the same panel opens as a
 *   bottom drawer, because a popover anchored to a chip in a wrapping toolbar
 *   has nowhere to go.
 *
 * AND ONE THING IT CHECKS (ruling d21, utils/idValueValidation.js): every
 * value with an id behind it — a choice, an item, a user, a tag — is verified where
 * it was typed, in a row or on the Advanced tab. A value that names nothing
 * (or several things) gets the warning border and a line under it offering
 * the matches, one click each; an item lookup's typed text is a title match
 * by design and is only NOTED, with the items that carry the title offered.
 * A choice's NAME typed where its stored value belongs is read as that
 * choice: the rows show it picked, and Apply writes the stored value. None
 * of it holds Apply — the API accepts all of these, and decides.
 *
 * AN OPERATOR THE API REFUSES does hold it (T7, ruled b26): `tags ~ x`,
 * `tags > x` — anywhere — and `tags is empty` where the filter decides who
 * sees what (the `tags` field in `access` mode). The rows never offer one;
 * typed or loaded, it is flagged where it stands ("Tags can only be matched
 * by is or is not.") and Apply waits until it changes.
 */
export default function FilterBuilder({
	fields = [],
	query = '',
	onApply,
	emptyText = DEFAULT_EMPTY_TEXT,
	placeholder = DEFAULT_PLACEHOLDER,
	triggerId,
	// The collection a lookup row picks items from, and the scope recent
	// filters are remembered under. A list with neither (cards, collections)
	// keeps the text box and shows no recents.
	collectionId = null,
	scopeKey = null,
	savedQueries = [],
	// A query with NO caller (a dynamic group's rule) cannot name `me`: the
	// API refuses it (400 `query_me_unsupported`), so the builder refuses it
	// first — the "Me" row and button are left out of the value inputs, and
	// a `me` typed on the Advanced tab holds Apply with the note.
	disallowMe = false,
	// What the builder says when it holds Apply for a `me`. The default is
	// the rule's reason; a surface whose reason differs (a collection link:
	// its readers are another collection's members) says its own.
	meDisallowedNote = ME_DISALLOWED_NOTE,
	// Which cards the active filter can return (utils/filterScope.js). It
	// SECTIONS the field menu and never narrows it.
	scopeCardIds = null,
	// Ids the server said name something removed (X-Query-Warnings
	// `removed`): the Advanced tab's id line reads "a removed card".
	removedIds = null,
	// Text the server resolves in a vocabulary this page cannot see whole —
	// who-sees-what text, read in every sub-collection too (element ids
	// step 3, ⚑ K4) — keeps writing an element's NAME, and the server asks
	// at save which element it means. Everywhere else a picked element is
	// written as its id (elementFieldEntries).
	keepElementNames = false,
}) {
	const [open, setOpen] = useState(false);
	const [tab, setTab] = useState('builder');
	// Does this collection have a card with an account link? Then a lookup
	// (or a facet) may be asked for "me" — the caller's own record. Read off
	// the collection's own vocabulary (the fields), not the app-wide cache.
	const collectionHasLink = useMemo(() => !!collectionId && fieldsHaveAccountLink(fields), [collectionId, fields]);
	// The top line's items: rows, and groups of rows one level down
	// (utils/conditionGroups.js), joined by `match` ('all' or 'any').
	const [rows, setRows] = useState([]);
	const [match, setMatch] = useState(MATCH_ALL);
	// True while the panel holds the applied query exactly as it was read: Apply
	// then hands back that text, so reading it into rows never reorders it.
	const [pristine, setPristine] = useState(true);
	const [text, setText] = useState('');
	const [advanced, setAdvanced] = useState(false);
	const [error, setError] = useState(null);
	// Bumped on every open so the recents strip re-reads localStorage (another
	// tab, or this one's last Apply, may have changed it).
	const [openedAt, setOpenedAt] = useState(0);
	// What each lookup row's item picker holds, keyed like the picker itself
	// (row, field, arity), so a row moved onto another lookup never judges
	// its value against the old card's items.
	const [lookupVocabularies, setLookupVocabularies] = useState({});
	// The vocabulary is read through a ref where the builder resets from the
	// applied query: `fields` may change identity on any parent render, and
	// a reset keyed on it would throw away a half-built filter.
	const fieldsRef = useRef(fields);
	fieldsRef.current = fields;
	// A user element's typed name is checked against the collection's
	// collaborators — fetched once per collection and session, and only
	// while the panel is open over a vocabulary that has a user element.
	const hasUserFields = useMemo(() => (fields || []).some(field => field?.dataType === 'user'), [fields]);
	const { users: collaborators } = useCollaborators(open && hasUserFields ? collectionId : null);
	const userVocabulary = useMemo(() => userVocabularyOf(collaborators), [collaborators]);

	const { compact: isPhone } = useWindowClass();
	const recentScope = scopeKey || collectionId;

	// The applied query is the source of truth; the panel is a scratch copy of
	// it that only becomes real on Apply.
	const resetFromQuery = useCallback(() => {
		const { ast, error: parseError } = parseForBuilder(query);
		setError(parseError || null);
		setPristine(true);
		if (parseError) {
			// A hand-edited/stale link: show it as typed, on the tab where it
			// can actually be fixed.
			setText(query || '');
			setRows([]);
			setMatch(MATCH_ALL);
			setAdvanced(false);
			setTab('advanced');
			return;
		}
		// A choice's NAME in a link (`status = "In progress"`) is read as the
		// choice: the row opens with it picked, and the text says what Apply
		// will send.
		const { ast: resolved } = resolveQueryLabels(ast, fieldsRef.current);
		// A pair of bounds the recognizer knows is ONE row again (readFilterGroups
		// collapses them), so a link carrying "this week" re-opens as the preset
		// rather than as two comparisons nobody typed — inside a group too.
		const tree = readFilterGroups(resolved, { fields: fieldsRef.current });
		setText(resolved ? serialize(resolved) : '');
		setAdvanced(tree === null);
		setRows(tree ? tree.items : []);
		setMatch(tree ? tree.match : MATCH_ALL);
	}, [query]);

	// A query that changes under an OPEN panel — the list adopting the
	// server's canonical spelling of what was just applied (F3) — leaves the
	// person's half-built filter alone; the panel reads the query again when
	// it next opens or is cancelled (the F3 review's F8).
	const openRef = useRef(open);
	openRef.current = open;
	useEffect(() => { if (!openRef.current) resetFromQuery(); }, [resetFromQuery]);

	// `nextMatch` only when the top line's join changes; otherwise it stays.
	const commitRows = useCallback((nextRows, nextMatch = match) => {
		setRows(nextRows);
		setMatch(nextMatch);
		setText(writeFilterGroups(nextRows, nextMatch));
		setError(null);
		setPristine(false);
	}, [match]);

	const fieldForRow = useCallback(row => findField(fields, row.path, row.forceElement, row.facet), [fields]);
	// A row opened on a field the menu does not offer — an element named by
	// its id (`#k7f3q2xa`, KPR-168: a chart's door, a filter saved with ids)
	// — still shows that field by its label, in a group of its own, rather
	// than the bare id the query carries.
	const { groups: offeredGroups, byValue: offeredFields } = useMemo(
		() => buildFieldOptions(fields, scopeCardIds, { keepElementNames }),
		[fields, scopeCardIds, keepElementNames]
	);
	const fieldOptions = useMemo(() => {
		const groups = offeredGroups;
		// A row on something the menu does not offer under that spelling — an
		// element named by a NAME the menu now offers by id (text saved
		// before), an id or a group nothing here holds — keeps its words.
		const strays = new Map();
		// Rows inside a group are rows of the filter too.
		allRows(rows).forEach((row) => {
			if (!Array.isArray(row.path)) return;
			const key = pathKey(row.path, row.forceElement, row.facet);
			if (offeredFields.has(key)) return;
			const field = fieldForRow(row);
			const named = !field.unknown || field.elementId || field.goneElement || field.elementGroup || (field.path || []).some(isElementIdSegment);
			if (!named || field.system || isFacetField(field)) return;
			if (!strays.has(key)) strays.set(key, { ...field, key });
		});
		if (!strays.size) return groups;
		return [...groups, {
			label: 'In this filter',
			options: [...strays.values()].map(field => ({
				value: field.key,
				label: (
					<span className="list-filter__field-option">
						<DataTypeBadge dataType={field.badgeType || field.dataType} iconOnly />
						<span className="list-filter__field-option-label">{field.label}</span>
					</span>
				),
				searchText: field.label,
				title: field.label,
			})),
		}];
	}, [offeredGroups, offeredFields, rows, fieldForRow]);
	// WHERE A ROW LIVES. A row of the filter reads its field in the page's
	// vocabulary (`topScope`). A row UNDER a repeating group's `has an entry
	// where…` (KPR-683) reads it in the group's columns, edits itself inside its
	// parent's `entryRows`, and offers no "compare with another field" (a block
	// holds no reference). Every handler below takes the scope its row is in.
	// (Rebuilt every render, not memoised: its `update` closes over this
	// render's rows, and a stale copy would write over the person's last edit.)
	const topScope = {
		nested: false,
		fields,
		offered: offeredFields,
		fieldFor: row => findField(fields, row.path, row.forceElement, row.facet),
		update: (row, patch) => updateRowById(row.id, patch),
	};
	const entryScopeFor = (parent, groupField) => {
		const columns = entryColumnFields(groupField);
		const { groups, byValue } = columnFieldOptions(columns, keepElementNames);
		return {
			nested: true,
			parent,
			fields: columns,
			groups,
			offered: byValue,
			fieldFor: row => findField(columns, row.path, row.forceElement, row.facet),
			update: (row, patch) => updateEntryRow(parent.id, row.id, patch),
		};
	};
	const labelFor = useCallback(
		(path, forceElement, facet, vocabulary) => findField(vocabulary || fields, path, forceElement, facet).label,
		[fields]
	);
	const valueLabelFor = useCallback(
		(path, forceElement, raw, facet, vocabulary) => conditionValueLabel(findField(vocabulary || fields, path, forceElement, facet), raw),
		[fields]
	);
	// A reference names an ELEMENT, and the chip has to say that element's own
	// label — never the slug the query carries, nor an id (`{{#k7f3q2xa}}`).
	const refLabelFor = useCallback(name => findField(fields, [name]).label, [fields]);
	// The "compare with" picker's options: the fields of the same family, and a
	// reference by id the query holds (element ids step 3's readers) shown as
	// its element — its label, never the bare id — and kept as that id until
	// the person picks another field.
	// A field picked here is written as the element itself (element ids
	// step 3, P4): `{{#k7f3q2xa}}` where one element answers to its name, as
	// a row's field is — the name where several do (the server asks which)
	// or where the text keeps names (`keepElementNames`).
	const refValueOf = (target) => {
		const defs = Array.isArray(target.definitions) ? target.definitions : [];
		return !keepElementNames && defs.length === 1 ? `#${defs[0].id}` : target.path[0];
	};
	const refOptions = (targets, raw) => {
		const options = targets.map(target => ({ value: refValueOf(target), label: target.label, searchText: target.label }));
		// A reference the text holds in another spelling — by id, or by a
		// name the menu now offers by id — keeps its words, as written.
		if (isRefRaw(raw) && !options.some(option => option.value === raw.name)) {
			const label = refLabelFor(raw.name);
			options.unshift({ value: raw.name, label, searchText: label });
		}
		return options;
	};

	// A row is found by its id wherever it sits: on the top line or in a group
	// of rows (KPR-978, utils/conditionGroups.js mapRows).
	function updateRowById(rowId, patch) {
		commitRows(mapRows(rows, row => (row.id === rowId ? { ...row, ...patch } : row)));
	}
	/** One row under a repeating group's `has` (KPR-683), edited in its parent's `entryRows`. */
	function updateEntryRow(parentId, entryRowId, patch) {
		commitRows(mapRows(rows, row => (row.id !== parentId ? row : {
			...row,
			entryRows: (row.entryRows || []).map(entry => (entry.id === entryRowId ? { ...entry, ...patch } : entry)),
		})));
	}

	const handleFieldChange = (row, nextKey, scope = topScope) => {
		// An element is offered by its id, or as a group (elementFieldEntries):
		// the row then writes `#id` — or the group — never the name.
		const nextField = scope.offered.get(nextKey) || (scope.fields || []).find(field => field.key === nextKey);
		if (!nextField) return;
		const operators = operatorsForField(nextField);
		const keepsOperator = !isSpanRow(row) && operators.some(option => option.value === row.cmp);
		scope.update(row, {
			path: [...nextField.path],
			forceElement: !!nextField.forceElement,
			// A facet and an element can spell the same root; the flag is what
			// says which condition the row is now writing.
			facet: !!nextField.facet,
			// A span belongs to the date it was picked for; another field
			// inherits the row, never its two bounds.
			kind: undefined,
			preset: undefined,
			parts: undefined,
			cmp: keepsOperator ? row.cmp : operators[0].value,
			// Values rarely survive a change of field type; dropping them beats
			// carrying a number into a choice. A reference target belonged to
			// the OLD field's type matrix and goes with it.
			valueSource: undefined,
			value: undefined,
			// A repeating group starts with its first column's row to fill in;
			// any other field carries none.
			entryRows: isEntriesField(nextField) ? seedRowsForGroup(nextField) : undefined,
		});
	};

	const handleOperatorChange = (row, nextCmp, scope = topScope) => {
		const field = scope.fieldFor(row);
		const wasRef = row.valueSource === VALUE_SOURCE_FIELD;
		const keepsRef = wasRef && refSupportsOperator(field.dataType, nextCmp);
		// Text to look for inside a value (`contains`) is not a choice, and a
		// choice row leaving it starts the value over rather than carrying a
		// substring into the choice menu as if somebody had picked it.
		const leavesSubstring = fieldKindOf(field) === 'choice' && isTextMatchOperator(row.cmp) && !isTextMatchOperator(nextCmp);
		scope.update(row, {
			cmp: nextCmp,
			valueSource: keepsRef ? VALUE_SOURCE_FIELD : undefined,
			// A row leaving field mode drops its target rather than carrying an
			// element name into a text box as if somebody had typed it.
			value: (wasRef && !keepsRef) || (leavesSubstring && !wasRef) ? undefined : retargetValue(row, nextCmp, field.dataType),
		});
	};

	const handleValueChange = (row, raw, scope = topScope) => {
		const field = scope.fieldFor(row);
		// A row over several elements writes an own unit by name (KPR-1109).
		scope.update(row, { value: makeValueNode(field.dataType, row.cmp, raw, field.elementGroup ? field.options : null) });
	};

	/**
	 * A date control's patch: `raw` is a plain value to wrap for the row's
	 * operator, everything else is the row's own shape (a span's parts, or the
	 * keys that clear one).
	 */
	const handleDatePatch = (row, patch, scope = topScope) => {
		const field = scope.fieldFor(row);
		const { raw, ...rest } = patch;
		const next = { ...rest };
		if ('raw' in patch) next.value = makeValueNode(field.dataType, row.cmp, raw);
		// A row that stops being a span needs an operator again.
		if ('kind' in next && !next.kind && isSpanRow(row)) {
			next.cmp = row.cmp || operatorsForField(field)[0].value;
		}
		scope.update(row, next);
	};

	const handleAddRow = () => {
		const first = fields[0];
		if (!first) return;
		commitRows([...rows, createRow(first)]);
	};

	const handleRemoveRow = rowId => commitRows(removeRow(rows, rowId));

	// GROUPS OF ROWS (KPR-978). A group starts with one row, joined the other
	// way from the top line (a group that joins like its parent adds nothing).
	const handleAddGroup = () => {
		const first = fields[0];
		if (!first) return;
		commitRows([...rows, makeGroup(groupMatchFor(match), [createRow(first)])]);
	};
	const handleAddGroupRow = (groupId) => {
		const first = fields[0];
		if (!first) return;
		commitRows(updateGroup(rows, groupId, conditionGroup => ({ ...conditionGroup, rows: [...(conditionGroup.rows || []), createRow(first)] })));
	};
	const handleGroupMatch = (groupId, next) => {
		commitRows(updateGroup(rows, groupId, conditionGroup => ({ ...conditionGroup, match: next })));
	};
	const handleRemoveGroup = groupId => commitRows(rows.filter(item => item.id !== groupId));
	const handleTopMatch = next => commitRows(rows, next);

	/** A new condition on the same entry: the group's first column, ready to fill in. */
	const handleAddEntryRow = (parent, groupField) => {
		const added = newEntryRowFor(groupField);
		if (!added) return;
		commitRows(mapRows(rows, row => (row.id === parent.id ? { ...row, entryRows: [...(row.entryRows || []), added] } : row)));
	};

	const handleRemoveEntryRow = (parent, entryRowId) => {
		commitRows(mapRows(rows, row => (row.id === parent.id ? { ...row, entryRows: (row.entryRows || []).filter(entry => entry.id !== entryRowId) } : row)));
	};

	const handleTextChange = nextText => {
		setText(nextText);
		setPristine(false);
		const { ast, error: parseError } = parseForBuilder(nextText);
		if (parseError) {
			// Never clobber the last good query: the rows stay as they were.
			setError(parseError);
			return;
		}
		setError(null);
		// The rows read a typed choice NAME as its choice; the text stays as
		// typed (it is never rewritten under the cursor), with a note.
		const { ast: resolved } = resolveQueryLabels(ast, fields);
		const tree = readFilterGroups(resolved, { fields });
		setAdvanced(tree === null);
		if (!tree) return;
		setRows(tree.items);
		setMatch(tree.match);
	};

	// Canonical text is what the URL (and later a saved filter) carries, so
	// even hand-typed queries are normalized on the way out — and a choice's
	// name becomes the stored value the API compares (ruling d21).
	// Unedited, the panel hands back the text it read (canonical, choice names
	// resolved), so opening a filter and applying it never reorders it.
	const appliedText = useMemo(
		() => resolveQueryTextLabels(advanced ? canonicalize(text) : (pristine ? text : writeFilterGroups(rows, match)), fields),
		[advanced, pristine, text, rows, match, fields]
	);
	const meBlocked = disallowMe && queryUsesMe(appliedText);
	// A condition whose operator the API refuses holds Apply (T7): unlike a
	// value's check, it is not advisory — the list or the setting would
	// answer 400 `unsupported_operator`.
	const operatorBlocked = useMemo(() => {
		const { ast } = parseForBuilder(appliedText);
		return ast ? checkQueryOperators(ast, fields).length > 0 : false;
	}, [appliedText, fields]);

	const apply = useCallback((nextQuery) => {
		if (recentScope) recordRecentFilter(recentScope, nextQuery);
		onApply(nextQuery || undefined);
		setOpen(false);
	}, [onApply, recentScope]);

	const handleApply = () => {
		if (error || meBlocked || operatorBlocked) return;
		apply(appliedText);
	};

	const handleClear = () => {
		setRows([]);
		setMatch(MATCH_ALL);
		setText('');
		setAdvanced(false);
		setError(null);
		onApply(undefined);
		setOpen(false);
	};

	const openPanel = useCallback((next) => {
		setOpen(next);
		if (next) { resetFromQuery(); setOpenedAt(Date.now()); }
	}, [resetFromQuery]);

	// The key a lookup row's picker (and so its vocabulary) is known by.
	const lookupKeyFor = (row, field) => `${row.id}:${field.key}:${isListOperator(row.cmp) ? 'many' : 'one'}`;

	const rememberLookupVocabulary = useCallback((key, vocabulary) => {
		setLookupVocabularies(prev => (prev[key] === vocabulary ? prev : { ...prev, [key]: vocabulary }));
	}, []);

	/**
	 * The id-backed values in one row, checked (utils/idValueValidation.js):
	 * what the row flags, or notes, under its value. A span, a reference and
	 * a row the Advanced tab owns say nothing here.
	 */
	const rowChecks = (row, field) => {
		if (advanced || isSpanRow(row)) return [];
		// An operator the API refuses (`tags ~ x`, emptiness where access is
		// decided — T7) is said first, and alone: the value can wait.
		const refused = checkOperator(field, row.cmp);
		if (refused) return [refused];
		if (!row.value) return [];
		const kind = idValueKindFor(field);
		if (!kind) return [];
		const vocabulary = kind === 'lookup'
			? lookupVocabularies[lookupKeyFor(row, field)]
			: (kind === 'user' ? userVocabulary : undefined);
		return checkConditionValues(field, row.cmp, row.value, { vocabulary })
			.map(check => ({ ...check, result: displayedCheck(check.result) }))
			.filter(check => check.result);
	};

	/** A one-click match: it replaces the value it was offered for, and only that one. */
	const handlePickMatch = (row, value, check, scope = topScope) => {
		const raw = rawValueOf(row.value);
		if (check.member !== null && check.member !== undefined && Array.isArray(raw)) {
			const next = raw.map((entry, index) => (index === check.member ? value : entry));
			handleValueChange(row, next.filter((entry, index) => next.indexOf(entry) === index), scope);
			return;
		}
		handleValueChange(row, value, scope);
	};

	/**
	 * The right-hand side, with its value/field toggle.
	 *
	 * The toggle appears ONLY when this field has at least one type-compatible
	 * partner and the operator has a field-to-field reading, so a query built
	 * here can never hit the incompatible-pair no-match — there is no warning
	 * channel on `?q=`, so "offer only what compiles" is the whole guarantee.
	 */
	const renderValueInput = (row, field, status, scope = topScope) => {
		// A repeating group's `has` holds rows, not a value (the panel under it).
		if (isNullaryOperator(row.cmp) || isEntriesCmp(row.cmp)) return null;
		// Inside a block there is nothing to compare a column with but a literal.
		const targets = scope.nested ? [] : refTargetsFor(field, fields);
		const canRef = !!targets.length && refSupportsOperator(field.dataType, row.cmp) && !isListOperator(row.cmp);
		const refMode = canRef && (row.valueSource === VALUE_SOURCE_FIELD || isRefRaw(rawValueOf(row.value)));
		const input = renderLiteralInput(row, field, refMode, targets, status, scope);
		if (!canRef) return input;
		return (
			<Space.Compact block className="list-filter__value-compact">
				{input}
				<Button
					type={refMode ? 'primary' : 'default'}
					aria-pressed={refMode}
					aria-label={`${field.label}: compare with another field`}
					title="Compare with another field of the same record"
					disabled={advanced}
					onClick={() => scope.update(row, {
						valueSource: refMode ? undefined : VALUE_SOURCE_FIELD,
						value: undefined,
					})}
				>
					{REF_TOGGLE_LABEL}
				</Button>
			</Space.Compact>
		);
	};

	// `status` is the row's check ('warning' when a value names nothing), worn
	// by whichever control holds the value.
	const renderLiteralInput = (row, field, refMode, targets, status, scope = topScope) => {
		const changeValue = value => handleValueChange(row, value, scope);
		if (refMode) {
			const raw = rawValueOf(row.value);
			return (
				<Select
					className="list-filter__value-input"
					showSearch
					filterOption={optionSearchFilter}
					placeholder={REF_PLACEHOLDER}
					aria-label={`${field.label}: compare with`}
					value={isRefRaw(raw) ? raw.name : undefined}
					options={refOptions(targets, raw)}
					disabled={advanced}
					onChange={name => changeValue(makeRefRaw(name))}
				/>
			);
		}
		const kind = fieldKindOf(field);
		// A hand-typed query can leave a reference on an operator this builder
		// offers no field mode for (`code > {{note}}`). The row keeps its value
		// — the query text is not rewritten behind anyone's back — but a
		// literal control must not try to render an element name as its value.
		const rawOrRef = rawValueOf(row.value);
		const raw = isRefRaw(rawOrRef) ? undefined : rawOrRef;
		const inputName = `filter-value-${row.id}`;

		// A part of a date picks from its values the same way (Sunday … Saturday).
		// A choice row holding `contains` (a filter saved when the element read as
		// text, KPR-142) keeps its words: what follows it is text to look for,
		// not a choice, so it falls through to the text box below.
		if ((kind === 'choice' && !isTextMatchOperator(row.cmp)) || kind === 'datePart') {
			const choices = choiceSelectOptions(field.options?.choices);
			if (isListOperator(row.cmp)) {
				return (
					<Select
						mode="multiple"
						className="list-filter__value-input"
						placeholder="Choose values"
						value={Array.isArray(raw) ? raw : []}
						options={choices}
						status={status}
						// The same matcher as the single-value InputSelect below:
						// antd's default would match the stored value, not the label.
						showSearch
						filterOption={optionSearchFilter}
						disabled={advanced}
						onChange={values => changeValue(values)}
					/>
				);
			}
			return (
				<InputSelect
					elementName={inputName}
					elementLabel=""
					value={raw ?? undefined}
					options={choices}
					status={status}
					disabled={advanced}
					onChange={(_name, value) => changeValue(value)}
				/>
			);
		}

		if (kind === 'tag') {
			// The tag picker (components/tags/TagSelect): the collection's
			// tags as their tree, my private tags where the query is mine
			// alone, item tags found by title — it writes the id and shows
			// the name (docs/plans/tags.md §5.2).
			//
			// Where the filter decides who sees what (`mode: 'access'`, T7) it
			// offers the RESTRICTED collection tags alone — no private tags, no
			// item tags — and, with none, says how to make one.
			const options = field.options || {};
			const multiple = isListOperator(row.cmp);
			const access = options.mode === 'access';
			return (
				<TagSelect
					key={`${row.id}:${multiple ? 'many' : 'one'}`}
					className="list-filter__value-input"
					multiple={multiple}
					value={multiple ? (Array.isArray(raw) ? raw : []) : (raw ?? undefined)}
					collectionTags={access ? (options.tags || []).filter(isRestrictedTag) : options.tags}
					privateTags={options.mode === 'live' ? options.privateTags : null}
					itemTags={!access}
					collectionHeading={access ? 'Restricted tags' : undefined}
					notFoundContent={access && !(options.tags || []).some(isRestrictedTag) ? NO_RESTRICTED_TAGS : undefined}
					itemTitles={options.itemTitles}
					status={status}
					disabled={advanced}
					aria-label={field.label}
					placeholder={multiple ? 'Choose tags' : 'Choose a tag'}
					onChange={next => changeValue(next)}
				/>
			);
		}

		if (kind === 'boolean') {
			const { trueLabel, falseLabel } = booleanLabels(field);
			return (
				<Select
					className="list-filter__value-input"
					placeholder="Yes or no"
					aria-label={field.label}
					value={typeof raw === 'boolean' ? raw : undefined}
					options={[{ value: true, label: trueLabel }, { value: false, label: falseLabel }]}
					disabled={advanced}
					onChange={value => changeValue(value)}
				/>
			);
		}

		if (kind === 'number' || kind === 'entryCount') {
			// A rating filters on its own scale: 0 to its stars, in whole or
			// half steps (utils/rating; data types second pass §3.1).
			const scale = field?.dataType === 'rating' ? ratingScale(field.options) : null;
			return (
				<InputNumber
					className="list-filter__value-input"
					value={raw ?? null}
					disabled={advanced}
					onChange={value => changeValue(value)}
					placeholder={scale ? `0–${scale.max}` : 'Value'}
					// A typed fraction ("1 1/2", "¾") is the number it is, as on the
					// item form (KPR-1025); the query holds the number.
					parser={text => parseNumberEntry(text)}
					inputMode={getNumericInputMode(field?.dataType)}
					{...(scale ? { min: 0, max: scale.max, step: scale.allowHalf ? 0.5 : 1 } : {})}
					{...(kind === 'entryCount' ? { min: 0, precision: 0, step: 1 } : {})}
				/>
			);
		}

		if (kind === 'measurement') {
			return (
				<MeasurementValueInput
					// Keyed by field: a row moved onto another element must open
					// a fresh control (and leave any text-mode fallback behind).
					key={`${row.id}:${field.key}`}
					id={inputName}
					className="list-filter__value-input"
					raw={raw}
					options={field.options}
					name={field.path.join('.')}
					label={field.label}
					aria-label={field.label}
					disabled={advanced}
					onChange={next => changeValue(next)}
				/>
			);
		}

		if (kind === 'currency') {
			return (
				<CurrencyValueInput
					key={`${row.id}:${field.key}`}
					id={inputName}
					className="list-filter__value-input"
					raw={raw}
					options={field.options}
					name={field.path.join('.')}
					label={field.label}
					aria-label={field.label}
					disabled={advanced}
					onChange={next => changeValue(next)}
				/>
			);
		}

		if (kind === 'user') {
			// An account is picked, never typed: the same picker the item form
			// uses, with "Me" as a row — the KEYWORD, resolved per reader.
			const multiple = isListOperator(row.cmp);
			return (
				<UserPickerSelect
					key={`${row.id}:${field.key}:${multiple ? 'many' : 'one'}`}
					className="list-filter__value-input"
					collectionId={collectionId}
					multiple={multiple}
					// A stored list can hold typed text where an id belongs; it is
					// shown and flagged, never sent to the by-id lookup (KPR-996).
					onlyAccountIds={multiple}
					meMode={disallowMe ? null : 'value'}
					value={multiple ? (Array.isArray(raw) ? raw : []) : raw}
					status={status}
					disabled={advanced}
					aria-label={field.label}
					placeholder={multiple ? 'Pick people' : 'Pick a person'}
					onChange={next => changeValue(next)}
				/>
			);
		}

		if (kind === 'lookup') {
			// The picker needs BOTH halves: which card the element points at,
			// and which collection to look in. Without them the free-text box
			// below is not a downgrade — it is the only thing that can be
			// authored, and the API resolves it against the item's title.
			const lookupCardId = field.options?.lookupCardId;
			if (lookupCardId && collectionId) {
				const multiple = isListOperator(row.cmp);
				const meRow = !multiple && isMeValue(raw);
				const picker = (
					<ItemLookupSelect
						key={`${row.id}:${field.key}:${multiple ? 'many' : 'one'}`}
						className="list-filter__value-input"
						collectionId={collectionId}
						cardId={lookupCardId}
						multiple={multiple}
						value={multiple ? (Array.isArray(raw) ? raw : []) : (meRow ? undefined : raw)}
						disabled={advanced || meRow}
						ariaLabel={field.label}
						placeholder={meRow ? 'Me (your own record)' : undefined}
						status={status}
						onVocabulary={vocabulary => rememberLookupVocabulary(lookupKeyFor(row, field), vocabulary)}
						onChange={next => changeValue(next)}
					/>
				);
				// "Me" — the reader's own linked record — beside the picker,
				// only where the collection has a card with an account link
				// and the query will have a reader to be.
				if (collectionHasLink && !multiple && !disallowMe) {
					return (
						<Space.Compact block className="list-filter__value-compact">
							{picker}
							<Button
								type={meRow ? 'primary' : 'default'}
								aria-pressed={meRow}
								aria-label={`${field.label}: me`}
								title="Your own record in this collection"
								disabled={advanced}
								onClick={() => changeValue(meRow ? undefined : ME_VALUE)}
							>
								Me
							</Button>
						</Space.Compact>
					);
				}
				return picker;
			}
		}

		if (kind === 'date') {
			// A `time` element holds a WALL CLOCK ('HH:mm'). It filters with
			// the date family's operators, but its literal is a clock and
			// nothing else: a 'YYYY-MM-DDTHH:mm' literal (what a date-time
			// row emits) can never match one, and neither can a relative
			// preset, so a time row opens straight on the clock.
			if (field.dataType === 'time') {
				// The clock literal rides as a quoted string (see
				// kqlBuilder.makeValueNode); a `date` node is what a query
				// hand-typed before this read as, and it binds just as well.
				const clock = row.value && (row.value.type === 'str' || row.value.type === 'date')
					? row.value.v
					: null;
				return (
					<div className="list-filter__date">
						<InputTime
							elementName={inputName}
							elementLabel=""
							value={clock}
							disabled={advanced}
							onChange={(_name, stored) => changeValue(stored || undefined)}
						/>
					</div>
				);
			}
			return (
				<FilterDateValue
					row={row}
					field={field}
					disabled={advanced}
					onPatch={patch => handleDatePatch(row, patch, scope)}
				/>
			);
		}

		return (
			<Input
				{...NO_TEXT_ASSIST}
				className="list-filter__value-input"
				value={raw ?? ''}
				status={status}
				disabled={advanced}
				placeholder={kind === 'lookup' ? 'Referenced item title' : 'Value'}
				onChange={event => changeValue(event.target.value)}
			/>
		);
	};

	/** A span row's "operator" is the period itself; there is nothing to choose. */
	const renderOperator = (row, field, scope = topScope) => {
		if (isSpanRow(row)) {
			return (
				<Select
					className="list-filter__operator"
					disabled
					value="within"
					options={[{ value: 'within', label: 'is within' }]}
				/>
			);
		}
		return (
			<Select
				className="list-filter__operator"
				disabled={advanced}
				value={row.cmp}
				options={operatorsForRow(field, row.cmp)}
				onChange={value => handleOperatorChange(row, value, scope)}
			/>
		);
	};

	const rowAriaLabel = (row, field, scope = topScope) => {
		if (isSpanRow(row)) return `Remove condition: ${field.label} ${spanLabel(row)}`;
		// The row's own comparison — a group as one ("Status · any of these
		// cards is Open"), never the `or` it is written as.
		const node = rowCondition(row);
		if (!node) return `Remove condition: ${field.label}`;
		// A row under a repeating group's `has` is read in the group's columns.
		const read = scope.nested
			? describeCondition(node, (path, forceElement) => findField(scope.fields, path, forceElement).label, (path, forceElement, raw) => conditionValueLabel(findField(scope.fields, path, forceElement), raw), refLabelFor, () => field)
			: describeCondition(node, labelFor, valueLabelFor, refLabelFor, () => field);
		return `Remove condition: ${read}`;
	};

	/**
	 * The field menu of a scope. Nested, it is the group's columns, plus any
	 * column a row still names that the group no longer offers (a column
	 * removed, or hidden from this person since the filter was saved), which
	 * keeps its words rather than showing a bare id.
	 */
	const optionsForScope = (scope, scopeRows) => {
		if (!scope.nested) return fieldOptions;
		const strays = new Map();
		(scopeRows || []).forEach((entry) => {
			const key = pathKey(entry.path, entry.forceElement, entry.facet);
			if (!scope.offered.has(key) && !strays.has(key)) strays.set(key, scope.fieldFor(entry));
		});
		if (!strays.size) return scope.groups;
		return [...scope.groups, {
			label: 'In this filter',
			options: [...strays].map(([key, stray]) => ({ value: key, label: stray.label, searchText: stray.label, title: stray.label })),
		}];
	};

	/** The conditions under a repeating group's `has an entry where…`: one panel, because they must hold on ONE entry. */
	const renderEntryPanel = (row, groupField) => {
		const scope = entryScopeFor(row, groupField);
		return (
			<div className="list-filter__entry" role="group" aria-label={`Conditions on one entry of ${groupField.label}`}>
				<p className="list-filter__entry-rule">
					{row.cmp === 'nhas' ? 'No entry matches all of these' : 'One entry matches all of these'}
				</p>
				{(row.entryRows || []).map(entry => renderRow(entry, scope, row.entryRows))}
				{!scope.fields.length ? <p className="list-filter__empty">This group has no column you can filter on.</p> : null}
				<Button
					className="list-filter__add list-filter__entry-add"
					size="small"
					icon={<PLUS />}
					disabled={advanced || !scope.fields.length}
					onClick={() => handleAddEntryRow(row, groupField)}
				>
					Add condition on the same entry
				</Button>
			</div>
		);
	};

	const renderRow = (row, scope = topScope, siblings = rows) => {
		const field = scope.fieldFor(row);
		const checks = rowChecks(row, field);
		const status = checks.some(check => isFlagged(check.result)) ? 'warning' : undefined;
		return (
			<React.Fragment key={row.id}>
				<div className={`list-filter__row${scope.nested ? ' list-filter__row--entry' : ''}`}>
					<Select
						className="list-filter__field"
						showSearch
						filterOption={optionSearchFilter}
						placeholder={scope.nested ? 'Column' : 'Field'}
						disabled={advanced}
						value={pathKey(row.path, row.forceElement, row.facet)}
						options={optionsForScope(scope, siblings)}
						onChange={value => handleFieldChange(row, value, scope)}
					/>
					{renderOperator(row, field, scope)}
					{/* A repeating group's `has` has no value: its conditions are the panel below. */}
					{isEntriesCmp(row.cmp) && isEntriesField(field) ? null : (
						<div className="list-filter__value">
							{renderValueInput(row, field, status, scope)}
							<IdValueCheck
								checks={checks}
								disabled={advanced}
								onPick={(value, check) => handlePickMatch(row, value, check, scope)}
							/>
						</div>
					)}
					<Button
						className="list-filter__remove"
						type="text"
						icon={<CLOSE />}
						disabled={advanced}
						onClick={() => (scope.nested ? handleRemoveEntryRow(scope.parent, row.id) : handleRemoveRow(row.id))}
						aria-label={rowAriaLabel(row, field, scope)}
					/>
				</div>
				{isEntriesCmp(row.cmp) && isEntriesField(field) ? renderEntryPanel(row, field) : null}
			</React.Fragment>
		);
	};

	/**
	 * A group of conditions (KPR-978): its own all / any / none, its rows, and
	 * its own Add. One level only: a group holds rows, never a group. Its rows
	 * are the top line's rows in every way — the same field menu, operators,
	 * value controls and checks — so a host's rules hold inside it.
	 */
	const renderGroup = (conditionGroup, groupIndex) => {
		const name = `Group ${groupIndex + 1}`;
		const groupRows = conditionGroup.rows || [];
		return (
			<div className="list-filter__group" role="group" aria-label={`${name}: ${MATCH_WORDS[conditionGroup.match]} of these`} key={conditionGroup.id}>
				<div className="list-filter__group-head">
					<Select
						className="list-filter__match-select"
						disabled={advanced}
						aria-label={`${name} matches`}
						value={conditionGroup.match}
						options={GROUP_MATCH_OPTIONS}
						onChange={next => handleGroupMatch(conditionGroup.id, next)}
					/>
					<span className="list-filter__match-words">of these</span>
					<Button
						className="list-filter__group-remove"
						type="text"
						icon={<CLOSE />}
						disabled={advanced}
						onClick={() => handleRemoveGroup(conditionGroup.id)}
						aria-label={`Remove group ${groupIndex + 1}`}
					/>
				</div>
				{groupRows.map(row => renderRow(row, topScope, groupRows))}
				{groupRows.length ? null : <p className="list-filter__empty">No conditions in this group yet.</p>}
				<Button
					className="list-filter__add list-filter__group-add"
					size="small"
					icon={<PLUS />}
					disabled={advanced || !fields.length}
					onClick={() => handleAddGroupRow(conditionGroup.id)}
				>
					Add condition to the group
				</Button>
			</div>
		);
	};

	let groupCount = 0;
	const builderPane = (
		<div className="list-filter__rows">
			{advanced ? (
				<Alert
					className="list-filter__note"
					type="info"
					showIcon
					message={ADVANCED_NOTE}
					action={<Button size="small" onClick={() => setTab('advanced')}>Edit as text</Button>}
				/>
			) : null}

			<RecentFilters
				scopeKey={recentScope}
				savedQueries={savedQueries}
				fields={fields}
				activeQuery={canonicalize(query)}
				refreshToken={openedAt}
				onApply={apply}
			/>

			{/* The top line, once there are two things to join. */}
			{rows.length > 1 ? (
				<div className="list-filter__match">
					<span className="list-filter__match-words">Match</span>
					<Select
						className="list-filter__match-select"
						disabled={advanced}
						aria-label="Match all or any of these"
						value={match}
						options={TOP_MATCH_OPTIONS}
						onChange={handleTopMatch}
					/>
					<span className="list-filter__match-words">of these</span>
				</div>
			) : null}

			{rows.map(item => (isGroupItem(item) ? renderGroup(item, groupCount++) : renderRow(item)))}

			{!rows.length && !advanced ? (
				<p className="list-filter__empty">{emptyText}</p>
			) : null}

			<div className="list-filter__adds">
				<Button
					className="list-filter__add"
					icon={<PLUS />}
					disabled={advanced || !fields.length}
					onClick={handleAddRow}
				>
					Add condition
				</Button>
				<Button
					className="list-filter__add list-filter__add-group"
					icon={<PLUS />}
					disabled={advanced || !fields.length}
					onClick={handleAddGroup}
				>
					Add a group
				</Button>
			</div>
		</div>
	);

	// The Advanced tab checks the text itself: every id-backed value in it,
	// wherever it sits (under an `or`, inside a `not`), each line naming its
	// field. A lookup's title here is noted without items to offer — the
	// pickers that fetch them live on the Builder tab.
	const textChecks = useMemo(() => {
		if (error) return [];
		const { ast } = parseForBuilder(text);
		if (!ast) return [];
		return [
			...checkQueryOperators(ast, fields).map(check => ({ ...check, fieldLabel: check.field.label })),
			...checkQueryValues(ast, fields, { vocabularies: { user: userVocabulary } })
				.map(check => ({ ...check, fieldLabel: check.field.label, result: displayedCheck(check.result, { labels: 'note' }) }))
				.filter(check => check.result),
		];
	}, [text, error, fields, userVocabulary]);

	// The line beneath the Advanced text naming each id it holds (ruling
	// E10): what a card, a set, a record or a tag id stands for, in words.
	// Records and people are named through the same batch resolve the chips
	// use, asked only while the Advanced tab is open.
	const advancedOpen = open && tab === 'advanced';
	const { titleFor: idTitleFor } = useQueryLookupTitles(advancedOpen && !error ? text : '', fields);
	const namedIds = useMemo(
		() => (advancedOpen && !error ? idsNamedIn(text, fields, { titleFor: idTitleFor, removedIds }) : []),
		[advancedOpen, error, text, fields, idTitleFor, removedIds]
	);

	const handlePickTextMatch = (value, check) => {
		const { ast } = parseForBuilder(text);
		if (!ast) return;
		handleTextChange(serialize(replaceQueryValue(ast, check.at, value)));
	};

	const advancedPane = (
		<div className="list-filter__advanced">
			<Input.TextArea
				{...NO_TEXT_ASSIST}
				className="list-filter__text"
				aria-label="Query text"
				autoSize={{ minRows: 3, maxRows: 10 }}
				value={text}
				onChange={event => handleTextChange(event.target.value)}
				placeholder={placeholder}
			/>
			{error ? (
				<div className="list-filter__error" role="alert">
					<div className="list-filter__error-message">
						{`Position ${error.position + 1}: ${error.message}`}
					</div>
					<pre className="list-filter__error-caret">
						{`${text}\n${' '.repeat(Math.max(0, error.position))}^`}
					</pre>
				</div>
			) : (
				<>
					<IdValueCheck checks={textChecks} onPick={handlePickTextMatch} />
					<p className="list-filter__hint">
						Canonical query text — this is exactly what the link carries.
					</p>
					<FilterIdsLine entries={namedIds} />
					<CopyTextButton text={text} size="small" />
				</>
			)}
		</div>
	);

	const panel = (
		<div className={`list-filter__panel${isPhone ? ' list-filter__panel--drawer' : ''}`}>
			<Tabs
				className="list-filter__tabs"
				activeKey={tab}
				onChange={setTab}
				items={[
					// Both panes stay mounted: switching tabs must not lose a
					// half-typed query or reset a row.
					{ key: 'builder', label: 'Builder', children: builderPane, forceRender: true },
					{ key: 'advanced', label: 'Edit as text', children: advancedPane, forceRender: true },
				]}
			/>
			{meBlocked ? (
				<Alert className="list-filter__note" type="warning" showIcon role="alert" message={meDisallowedNote} />
			) : null}
			{operatorBlocked ? (
				<Alert className="list-filter__note" type="warning" showIcon role="alert" message={REFUSED_OPERATOR_NOTE} />
			) : null}
			<div className="list-filter__actions">
				<Button onClick={handleClear} disabled={!query && !text}>Clear</Button>
				<Space>
					<Button onClick={() => { resetFromQuery(); setOpen(false); }}>Cancel</Button>
					<Button type="primary" onClick={handleApply} disabled={!!error || meBlocked || operatorBlocked}>Apply</Button>
				</Space>
			</div>
		</div>
	);

	// Applied conditions already show as chips beside this trigger, so no
	// count badge — the dashed chip only ADDS a condition.
	const trigger = (
		<Button
			id={triggerId}
			className="list-filter__button view-chip view-chip--add"
			type="text"
			onClick={isPhone ? () => openPanel(true) : undefined}
		>
			+ Filter
		</Button>
	);

	// A phone gets the drawer: a popover has nowhere to sit in a wrapping toolbar.
	if (isPhone) {
		return (
			<>
				{trigger}
				<Drawer
					open={open}
					placement="bottom"
					height="auto"
					className="list-filter__drawer"
					title="Filter"
					onClose={() => openPanel(false)}
					destroyOnClose
				>
					{panel}
				</Drawer>
			</>
		);
	}

	return (
		<Popover
			open={open}
			onOpenChange={openPanel}
			trigger="click"
			placement="bottomLeft" autoAdjustOverflow={{ adjustX: true, adjustY: true, shiftX: true }}
			destroyTooltipOnHide
			content={panel}
			overlayClassName="list-filter__popover"
		>
			{trigger}
		</Popover>
	);
}
